import { useCallback } from "react";

import { useAnalytics } from "@/common/analytics";
import { detectClientPlatform } from "@/common/detectPlatform";
import { useCopy } from "./useCopy";

/**
 * useCopy for install commands, with the funnel event attached.
 *
 * The landing pages are the top of the CLI install funnel and were entirely
 * invisible: a visitor copying `npm install -g @degausai/wonda` produced no
 * event at all, so every CLI install that did not go through /install.sh
 * appeared to come from nowhere. Wrapping the hook rather than editing each
 * button keeps the call sites to a one-line change and stops the next new
 * landing variant from silently reintroducing the gap.
 */

/** Which install mechanism the copied text represents. */
function detectMethod(text: string): "npm_copy" | "curl_copy" | "prereq_copy" {
  const value = text.toLowerCase();
  if (value.includes("curl") || value.includes("install.sh"))
    return "curl_copy";
  if (value.includes("npm ")) return "npm_copy";
  return "prereq_copy";
}

/**
 * Drop-in replacement for `useCopy` on install-command buttons.
 *
 * `placement` names the spot within the landing surface (e.g. "hero",
 * "how_it_works") so buttons stay separable without widening the surface enum.
 */
export function useInstallCopy(
  placement: string,
): [boolean, (text: string) => void] {
  const [copied, copy] = useCopy();
  const trackEvent = useAnalytics();

  const trackedCopy = useCallback(
    (text: string) => {
      copy(text);
      trackEvent("install_cta_clicked", {
        surface: "landing",
        method: detectMethod(text),
        os: detectClientPlatform(),
        placement,
      });
    },
    [copy, trackEvent, placement],
  );

  return [copied, trackedCopy];
}
